/**
 * 門市據點查詢
 * 從後台設定的 CSV 網址讀取門市資料，提供縣市下拉與關鍵字即時過濾。
 * CSV 標題列可用中文或英文別名，欄位順序不限。
 */
if (!customElements.get('store-locator')) {
  // 縣市下拉的排序依據（由北到南）；不在表內的縣市排在最後，依 CSV 出現順序。
  const CITY_ORDER = [
    '基隆市',
    '臺北市',
    '新北市',
    '桃園市',
    '新竹市',
    '新竹縣',
    '苗栗縣',
    '臺中市',
    '彰化縣',
    '南投縣',
    '雲林縣',
    '嘉義市',
    '嘉義縣',
    '臺南市',
    '高雄市',
    '屏東縣',
    '宜蘭縣',
    '花蓮縣',
    '臺東縣',
    '澎湖縣',
    '金門縣',
    '連江縣',
  ];

  // CSV 標題 → 內部欄位名稱。比對前會轉小寫並去除空白。
  const HEADER_ALIASES = {
    店名: 'name',
    門市: 'name',
    門市名稱: 'name',
    名稱: 'name',
    name: 'name',
    地址: 'address',
    address: 'address',
    電話: 'phone',
    phone: 'phone',
    tel: 'phone',
    縣市: 'city',
    城市: 'city',
    地區: 'city',
    city: 'city',
    region: 'city',
    網址: 'url',
    地圖: 'url',
    地圖網址: 'url',
    url: 'url',
    map: 'url',
    map_url: 'url',
  };

  const ICONS = {
    pin: 'M12 2c-3.87 0-7 3.13-7 7 0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z',
    phone:
      'M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.02-.24c1.12.37 2.33.57 3.57.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1C10.85 21 3 13.15 3 3.5a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.24.2 2.45.57 3.57a1 1 0 0 1-.25 1.02l-2.2 2.2z',
    map: 'M20.5 3.5 15 5.5 9 3.5 3.5 5.4v15.1L9 18.5l6 2 5.5-1.9V3.5zM9 16.4V5.6l6 2v10.8l-6-2z',
    search:
      'M15.5 14h-.79l-.28-.27a6.47 6.47 0 0 0 1.57-4.23 6.5 6.5 0 1 0-6.5 6.5c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9z',
  };

  const SVG_NS = 'http://www.w3.org/2000/svg';

  function makeIcon(path, className) {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', className);
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'currentColor');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    const node = document.createElementNS(SVG_NS, 'path');
    node.setAttribute('d', path);
    svg.appendChild(node);
    return svg;
  }

  class StoreLocator extends HTMLElement {
    /**
     * 解析 CSV 文字為 { name, address, phone, city, url } 陣列。
     * 支援雙引號包覆、引號內的逗號與換行、"" 跳脫、CRLF；忽略店名為空的列。
     */
    static parseCsv(text) {
      const rows = StoreLocator.splitRows(text.replace(/^﻿/, ''));
      if (!rows.length) return [];

      const map = rows[0].map((title) => HEADER_ALIASES[title.trim().toLowerCase()] || null);
      if (!map.includes('name')) return [];

      const stores = [];
      for (let i = 1; i < rows.length; i++) {
        const store = { name: '', address: '', phone: '', city: '', url: '' };
        rows[i].forEach((value, index) => {
          const key = map[index];
          if (key) store[key] = String(value).trim();
        });
        if (store.name) stores.push(store);
      }
      return stores;
    }

    /** 逐字元掃描，回傳二維陣列。 */
    static splitRows(text) {
      const rows = [];
      let row = [];
      let field = '';
      let quoted = false;

      for (let i = 0; i < text.length; i++) {
        const char = text[i];

        if (quoted) {
          if (char === '"') {
            if (text[i + 1] === '"') {
              field += '"';
              i++;
            } else {
              quoted = false;
            }
          } else {
            field += char;
          }
          continue;
        }

        if (char === '"') {
          quoted = true;
        } else if (char === ',') {
          row.push(field);
          field = '';
        } else if (char === '\n' || char === '\r') {
          if (char === '\r' && text[i + 1] === '\n') i++;
          row.push(field);
          field = '';
          if (row.some((cell) => cell.trim() !== '')) rows.push(row);
          row = [];
        } else {
          field += char;
        }
      }

      row.push(field);
      if (row.some((cell) => cell.trim() !== '')) rows.push(row);
      return rows;
    }

    /** 先以 UTF-8 解碼，出現替換字元時改用 Big5（Excel 匯出常見）。 */
    static decode(buffer) {
      const utf8 = new TextDecoder('utf-8').decode(buffer);
      if (!utf8.includes('�')) return utf8;
      try {
        return new TextDecoder('big5').decode(buffer);
      } catch (error) {
        return utf8;
      }
    }

    /** 「台」「臺」視為相同字，供縣市比對與排序使用。 */
    static normalizeCity(city) {
      return String(city || '')
        .trim()
        .replace(/台/g, '臺');
    }

    constructor() {
      super();
      this.stores = [];
      this.filtered = [];
      this.visibleCount = 0;
      this.debounceTimer = null;
    }

    connectedCallback() {
      // 元素被搬移時會再次觸發，只重跑載入、不重複綁事件。
      if (this.initialized) {
        if (!this.stores.length) this.load();
        return;
      }
      this.initialized = true;

      this.pageSize = parseInt(this.dataset.pageSize, 10) || 0;
      this.mapLinkLabel = this.dataset.mapLinkLabel || '店家位置';
      this.emptyText = this.dataset.emptyText || '查無符合條件的門市';
      this.errorText = this.dataset.errorText || '門市資料暫時無法載入，請稍後再試';
      // 允許後台留空表示不顯示筆數，因此不用 || 帶預設值。
      this.countText = this.dataset.countText === undefined ? '共 {count} 間門市' : this.dataset.countText;

      this.form = this.querySelector('[data-role="search-form"]');
      this.keywordInput = this.querySelector('[data-role="keyword"]');
      this.citySelect = this.querySelector('[data-role="city"]');
      this.statusEl = this.querySelector('[data-role="status"]');
      this.resultsEl = this.querySelector('[data-role="results"]');
      this.moreButton = this.querySelector('[data-role="more"]');

      this.bindEvents();
      this.load();
    }

    disconnectedCallback() {
      clearTimeout(this.debounceTimer);
      if (this.controller) this.controller.abort();
    }

    bindEvents() {
      if (this.form) {
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.applyFilters();
        });
      }
      if (this.keywordInput) {
        this.keywordInput.addEventListener('input', () => {
          clearTimeout(this.debounceTimer);
          this.debounceTimer = setTimeout(() => this.applyFilters(), 150);
        });
      }
      if (this.citySelect) {
        this.citySelect.addEventListener('change', () => this.applyFilters());
      }
      if (this.moreButton) {
        this.moreButton.addEventListener('click', () => {
          this.visibleCount += this.pageSize;
          this.renderResults();
        });
      }
      const downloadLink = this.querySelector('[data-role="download-sample"]');
      if (downloadLink) {
        downloadLink.addEventListener('click', (event) => this.downloadSample(event));
      }
    }

    /**
     * 範例 CSV 放在 assets，網址是 cdn.shopify.com，跟商店不同來源，
     * 這時瀏覽器會忽略 <a download>：自訂檔名失效，要不要下載也全看回應標頭，
     * 而 Shopify CDN 不會回 Content-Disposition，結果多半是開新分頁而非下載。
     * 先抓成 blob 換成同源的 blob: 網址，download 屬性才會生效。
     */
    async downloadSample(event) {
      const link = event.currentTarget;
      const filename = link.dataset.downloadName || 'store-locator-sample.csv';
      event.preventDefault();

      let objectUrl = '';
      try {
        const response = await fetch(link.href, { cache: 'no-store' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        objectUrl = URL.createObjectURL(await response.blob());
      } catch (error) {
        console.error('[store-locator] 下載範例 CSV 失敗:', error);
        window.open(link.href, '_blank', 'noopener');
        return;
      }

      const trigger = document.createElement('a');
      trigger.href = objectUrl;
      trigger.download = filename;
      document.body.appendChild(trigger);
      trigger.click();
      trigger.remove();
      // 立即 revoke 會讓部分瀏覽器來不及取檔，延後釋放。
      setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
    }

    async load() {
      const url = this.dataset.csvUrl;

      if (!url) {
        this.setStatus(
          window.Shopify && window.Shopify.designMode
            ? '尚未設定 CSV 檔案網址，請在此區塊的設定中填入門市資料的檔案連結。'
            : '',
          'notice'
        );
        return;
      }

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      try {
        const response = await fetch(url, { signal: this.controller.signal, cache: 'no-store' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const buffer = await response.arrayBuffer();
        this.stores = StoreLocator.parseCsv(StoreLocator.decode(buffer));
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('[store-locator] 讀取 CSV 失敗:', error);
        this.setStatus(this.errorText, 'error');
        return;
      }

      if (!this.stores.length) {
        this.setStatus(this.errorText, 'error');
        return;
      }

      this.populateCities();
      this.applyFilters();
    }

    populateCities() {
      if (!this.citySelect) return;

      const seen = new Map();
      this.stores.forEach((store) => {
        const key = StoreLocator.normalizeCity(store.city);
        if (key && !seen.has(key)) seen.set(key, store.city.trim());
      });

      const cities = [...seen.entries()].sort((a, b) => {
        const rankA = CITY_ORDER.indexOf(a[0]);
        const rankB = CITY_ORDER.indexOf(b[0]);
        if (rankA === -1 && rankB === -1) return 0;
        if (rankA === -1) return 1;
        if (rankB === -1) return -1;
        return rankA - rankB;
      });

      cities.forEach(([key, label]) => {
        const option = document.createElement('option');
        option.value = key;
        option.textContent = label;
        this.citySelect.appendChild(option);
      });
    }

    applyFilters() {
      const city = this.citySelect ? this.citySelect.value : '';
      const keyword = this.keywordInput ? this.keywordInput.value.trim().toLowerCase() : '';

      this.filtered = this.stores.filter((store) => {
        if (city && StoreLocator.normalizeCity(store.city) !== city) return false;
        if (!keyword) return true;
        const haystack = `${store.name} ${store.address} ${store.city} ${store.phone}`.toLowerCase();
        return haystack.includes(keyword);
      });

      this.visibleCount = this.pageSize > 0 ? this.pageSize : this.filtered.length;
      this.renderResults();
    }

    renderResults() {
      if (!this.resultsEl) return;

      this.resultsEl.textContent = '';

      if (!this.filtered.length) {
        this.setStatus(this.emptyText);
        if (this.moreButton) this.moreButton.hidden = true;
        return;
      }

      const limit = Math.min(this.visibleCount, this.filtered.length);
      const fragment = document.createDocumentFragment();
      for (let i = 0; i < limit; i++) {
        fragment.appendChild(this.buildCard(this.filtered[i]));
      }
      this.resultsEl.appendChild(fragment);

      this.setStatus(this.countText.replace('{count}', this.filtered.length));
      if (this.moreButton) this.moreButton.hidden = limit >= this.filtered.length;
    }

    buildCard(store) {
      const card = document.createElement('article');
      card.className = 'store-card';

      const name = document.createElement('h3');
      name.className = 'store-card__name';
      name.textContent = store.name;
      card.appendChild(name);

      const meta = document.createElement('ul');
      meta.className = 'store-card__meta';

      if (store.address) {
        meta.appendChild(this.buildRow(ICONS.pin, store.address, 'pin'));
      }

      if (store.phone) {
        const link = document.createElement('a');
        link.href = `tel:${store.phone.replace(/[^\d+#*,;]/g, '')}`;
        link.textContent = store.phone;
        meta.appendChild(this.buildRow(ICONS.phone, link, 'phone'));
      }

      const mapUrl = this.buildMapUrl(store);
      if (mapUrl) {
        const link = document.createElement('a');
        link.className = 'store-card__map';
        link.href = mapUrl;
        link.target = '_blank';
        link.rel = 'noopener';
        link.textContent = this.mapLinkLabel;
        meta.appendChild(this.buildRow(ICONS.map, link, 'map'));
      }

      card.appendChild(meta);
      return card;
    }

    buildRow(iconPath, content, iconName) {
      const row = document.createElement('li');
      row.className = 'store-card__row';
      row.appendChild(makeIcon(iconPath, `icon store-card__icon store-card__icon--${iconName}`));

      if (typeof content === 'string') {
        const span = document.createElement('span');
        span.textContent = content;
        row.appendChild(span);
      } else {
        row.appendChild(content);
      }
      return row;
    }

    /**
     * 地圖連結取用順序：CSV 的「網址」欄 → 地址查詢；兩者都沒有就不顯示連結。
     */
    buildMapUrl(store) {
      const direct = StoreLocator.safeUrl(store.url);
      if (direct) return direct;

      if (store.address) {
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(store.address)}`;
      }
      return '';
    }

    /** CSV 是外部資料，只放行 http/https，擋掉 javascript: 之類的協定。 */
    static safeUrl(value) {
      const raw = String(value || '').trim();
      if (!raw) return '';
      try {
        // 不帶 base，相對路徑或非網址的文字會直接拋錯而被擋下。
        const parsed = new URL(raw);
        return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : '';
      } catch (error) {
        return '';
      }
    }

    setStatus(message, modifier) {
      if (!this.statusEl) return;
      this.statusEl.className = `store-locator__status${modifier ? ` store-locator__status--${modifier}` : ''}`;
      this.statusEl.textContent = message;
      this.statusEl.hidden = !message;
    }
  }

  customElements.define('store-locator', StoreLocator);
}
